import { createContext, useContext, useEffect, useState } from 'react'

import { DocumentValidationErrorType } from '../../utils'
import { Nullable } from '../../utils/types'
import { IdDocumentValidation_ViewerFragment } from './__generated__/id-document-validation.graphql'
import { Steps } from './enums'
import { IdDocumentValidationEventData, IdDocumentValidationTexts } from './types'

export interface ModalContextProps {
  setCurrentStep: (currentStep: Steps) => void
  children: React.ReactNode
  eventData: IdDocumentValidationEventData
  viewer: IdDocumentValidation_ViewerFragment | null
  validationOrigin: string
  texts?: IdDocumentValidationTexts
  onAfterValidationSuccess?: () => void | Promise<void>
}

export interface SharedDataInterface {
  cpf?: string
  birthDate?: Date
  documentFile?: File
  documentData?: string
  documentName?: string
  errorType?: DocumentValidationErrorType
  errorMessage?: string
  profileInUse?: {
    pid: number
    email: string
  }
}

export interface ContextDataInterface {
  setCurrentStep: (currentStep: Steps) => void
  sharedData: SharedDataInterface
  setSharedData: (data: SharedDataInterface) => void
  eventData: IdDocumentValidationEventData
  viewer: IdDocumentValidation_ViewerFragment | null
  validationOrigin: string
  texts?: IdDocumentValidationTexts
  onAfterValidationSuccess?: () => void | Promise<void>
}

const ModalContext = createContext<Nullable<ContextDataInterface>>(null)

export const useIdDocumentValidationContext = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error(
      'useIdDocumentValidationContext must be used within IdDocumentValidationContextProvider'
    )
  }
  return context
}

export function IdDocumentValidationContextProvider(props: ModalContextProps) {
  const {
    setCurrentStep,
    children,
    viewer,
    eventData,
    validationOrigin,
    texts,
    onAfterValidationSuccess,
  } = props

  const [sharedData, setSharedData] = useState<SharedDataInterface>({})

  useEffect(() => {
    const birthDate = viewer?.birthDate
    const cpf = viewer?.cpf

    if (!cpf || birthDate == null) {
      return
    }

    setSharedData((current) => ({
      ...current,
      cpf: current.cpf ?? cpf,
      birthDate: current.birthDate ?? new Date(birthDate),
    }))
  }, [viewer?.cpf, viewer?.birthDate])

  const contextData = {
    setCurrentStep,
    sharedData,
    setSharedData,
    eventData,
    viewer,
    validationOrigin,
    texts,
    onAfterValidationSuccess,
  }

  return <ModalContext.Provider value={contextData}>{children}</ModalContext.Provider>
}
